import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  getStoreProductsApi,
  getStoreBrandsApi,
  saveFBTLinksApi,
  removeFBTLinkApi
} from 'api';
import {
  Button,
  CircularProgress,
  Drawer,
  Tooltip,
  TableContainer,
  Table,
  TableCell,
  TableRow,
  TableHead,
  TableBody,
  Paper,
  Icon,
  IconButton,
  LinearProgress,
  Chip
} from '@material-ui/core';
import MUIDataTable from 'mui-datatables';
import NumberFormat from 'react-number-format';
import logo from 'components/Logo/logo-dermpro.svg';
import { imgBaseUrl } from 'configs';
import { BackdropCircular } from 'components';


const FrequentlyBoughtTogether = ({product}) => {
  const dispatch = useDispatch();
  const [openFBTDrawer, setOpenFBTDrawer] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [filteredCatalog, setFilteredCatalog] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const {
    fbtData,
    store_products: {catalogProducts},
    productsLoading,
    removeFBTLinksLoading,
    saveFBTLinksLoading
  } = useSelector((state) => state.products);
  const { storeBrands } = useSelector((state) => state.brands);
  const { stores = [] } = useSelector((state) => state.user.profile);
  const fbtSkus = fbtData.map((item) => item.linked_product_sku);
  const mediaStoreUrl = `${stores[0]?.apiUrl}/media/catalog/product` || imgBaseUrl;

  useEffect(() => {
    if (catalogProducts.length === 0) {
      getStoreProductsApi({dispatch});
    }

    if (storeBrands.length === 0) {
      getStoreBrandsApi({dispatch});
    }
  }, []);


  useEffect(() => {
    if (catalogProducts?.length && storeBrands?.length) {
      const filteredCatalog = catalogProducts.filter((p) => {
        return fbtSkus.length === 0 ?
          product.sku !== p.sku
          :
          product.sku !== p.sku &&
          !fbtSkus.includes(p.sku);
      });

      setFilteredCatalog([...filteredCatalog.map((product) => {
        const productImagePath = product?.custom_attributes?.find((attr) => attr.attribute_code === 'image')?.value;
        const productBrand = product?.custom_attributes?.find((attr) => attr.attribute_code === 'brand')?.value;
        return {
          ...product,
          image: productImagePath ? `${mediaStoreUrl}${productImagePath}` : logo,
          price: +Number.parseFloat(product?.price).toFixed(2) || 0,
          brand: storeBrands?.find((b) => b.option_id === productBrand)?.value || '',
          status: product?.status === 1 ? 'Enabled' : 'Disabled'
        };
      })]);

      setCatalog([...catalogProducts.map((product) => {
        const productImagePath = product?.custom_attributes?.find((attr) => attr.attribute_code === 'image')?.value;
        const productBrand = product?.custom_attributes?.find((attr) => attr.attribute_code === 'brand')?.value;
        return {
          ...product,
          image: productImagePath ? `${mediaStoreUrl}${productImagePath}` : logo,
          price: +Number.parseFloat(product?.price).toFixed(2) || '-',
          brand: storeBrands?.find((b) => b.option_id === productBrand)?.value || ''
        };
      })]);
    }
  }, [catalogProducts.length, fbtData.length, storeBrands.length]);

  const handleSaveFBTLinks = async (selectedRows) => {
    setIsLoading(true);
    const selectedIndexes = Object.entries(selectedRows.lookup).filter((key) => key).map((i) => i[0]);
    const selectedData = selectedIndexes.map((idx) => filteredCatalog[idx]);
    const selectedSkus = selectedData.map((i) => i.sku);
    const {sku} = product;

    saveFBTLinksApi({dispatch, sku, selectedSkus});
    setOpenFBTDrawer(false);
    setIsLoading(false);
  };

  const BackButton = () => (
    <IconButton
      style={{marginLeft: '-16px'}}
      title="Close"
      onClick={() => setOpenFBTDrawer(false)}
    >
      <Icon>close</Icon>
    </IconButton>
  );

  const columns = [
    {
      name: 'sku',
      label: 'Item Name',
      options: {
        sort: false,
        filter: false,
        display: false
      },
    },
    {
      name: 'name',
      label: 'Item Name',
      options: {
        sort: true,
        filter: true,
        customBodyRenderLite: (dataIndex) => {
          const product = filteredCatalog[dataIndex];
          return (
            <div className="flex items-center">
              <img
                src={loaded ? product.image : logo}
                alt={product.name}
                style={{maxHeight: '60px', width: '60px', objectFit: 'contain'}}
                onLoad={() => setLoaded(true)}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = logo;
                }}
              />
              <div className="ml-3">
                <p className="my-0 text-15" >{product.name}</p>
              </div>
            </div>
          );
        },
      },
    },
    {
      name: 'brand',
      label: 'Brand',
      options: {
        filter: true,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const product = filteredCatalog[dataIndex];
          return (
            <div className="flex items-center">
              <div className="ml-3">
                <p className="my-0 text-15">{product.brand}</p>
              </div>
            </div>
          );
        },
      },
    },
    {
      name: 'price',
      label: 'Price',
      options: {
        filter: true,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const product = filteredCatalog[dataIndex];
          const price = product.price
            ? (
              <NumberFormat
                value={product.price}
                displayType="text"
                fixedDecimalScale
                decimalScale={2}
                prefix="$"
              />
            )
            : '-';
          return (
            <div className="flex items-center">{price}</div>
          );
        },
      },
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        filter: true,
        sort: true,
        filterType: 'dropdown',
        customBodyRenderLite: (dataIndex) => {
          const product = filteredCatalog[dataIndex];
          return (
            <Chip label={product.status} color={product.status === 'Enabled' ? 'primary' : 'grey'} />
          );
        },
      },
    },
  ];

  return (
    <div className="m-4 w-full">
      {!fbtData.length && !productsLoading && <div className="text-muted">You don&apos;t have linked FBT items.</div>}
      {productsLoading && <LinearProgress className="my-4" />}
      {!!fbtData.length && !!catalog.length &&
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Brand</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fbtData.map((item) => {
              const {linked_product_sku} = item;
              const linkedProduct = catalog.find((product) => product.sku === linked_product_sku);

              if (!linkedProduct) {
                return (
                  <TableRow key={linked_product_sku}>
                    <TableCell colSpan={4}>
                      Can&apos;t find product linked product with such SKU ({linked_product_sku}). Please contact our support team to fix this issue.
                    </TableCell>
                  </TableRow>
                );
              }

              return (
                <TableRow key={linked_product_sku}>
                  <TableCell>
                    <div className="flex items-center">
                      <div className="w-full max-w-60 text-center">
                        <img
                          src={linkedProduct.image}
                          alt={linkedProduct.name}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = logo;
                          }}
                          className="max-h-60 max-w-60"
                        />
                      </div>
                      <div className="px-2">{linkedProduct.name}</div>
                    </div>
                  </TableCell>
                  <TableCell>{linkedProduct.brand}</TableCell>
                  <TableCell>
                    {linkedProduct.price
                      ? (
                        <NumberFormat
                          value={linkedProduct.price}
                          displayType="text"
                          fixedDecimalScale
                          decimalScale={2}
                          prefix="$"
                        />
                      )
                      : '-'
                    }
                  </TableCell>
                  <TableCell>
                    {
                      <Chip
                        label={linkedProduct.status == 1 ? 'Enabled' : 'Disabled'}
                        color={linkedProduct.status === 1 ? 'primary' : 'grey'}
                      />
                    }
                  </TableCell>
                  <TableCell>
                    <Button
                      color="primary"
                      variant="outlined"
                      onClick={() => removeFBTLinkApi({dispatch, sku: product.sku, linkedProductSku: linked_product_sku})}
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      }
      <Button
        variant="contained"
        className="bg-light-primary my-4"
        onClick={() => setOpenFBTDrawer(true)}
      >
        Add Frequently Bought Together Items
      </Button>
      <Drawer
        open={openFBTDrawer}
        onClose={() => setOpenFBTDrawer(false)}
        anchor="right"
      >
        {productsLoading ?
          <div className="flex-column items-center justify-center m-20">
            <CircularProgress className="mb-10" />
            Catalog Items Data Is Loading...
          </div>
          :
          <>
            <MUIDataTable
              title={<BackButton />}
              data={filteredCatalog}
              columns={columns}
              options={{
                filterType: 'textField',
                responsive: 'standard',
                selectableRows: 'multiple',
                fixedHeader: true,
                tableBodyHeight: '90vh',
                search: true,
                filter: true,
                download: false,
                print: false,
                pagination: true,
                viewColumns: false,
                rowsPerPageOptions: [10, 20, 40, 80, 100],
                customToolbarSelect: (selectedRows, displayData) => {
                  return (
                    <Tooltip title={'Publish Selected'} className="m-2">
                      <Button variant="contained" color="primary" onClick={() => handleSaveFBTLinks(selectedRows, displayData)}>
                        Add Selected
                      </Button>
                    </Tooltip>
                  );
                }
              }}
              className="pl-10"
            />
            <BackdropCircular loading={isLoading} />
          </>
        }
      </Drawer>
      <BackdropCircular loading={removeFBTLinksLoading || saveFBTLinksLoading} />
    </div>
  );
};

export default FrequentlyBoughtTogether;
